import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  genericHeliosRequest,
  HeliosServerSideRequest,
  SetFilter,
} from '@helios/server-side-util'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Observable } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { ExternalPositionDetailResponse } from '../model/external-position-detail.model'
import {
  HistoricalBalanceComparison,
  HistoricalBalanceComparisonExtToExt,
} from '../model/historical-balance-comparison.model'
import {
  PositionComparisons,
  PositionComparisonsExtToExt,
} from '../model/position-comparison.model'
import {
  ExportExcelRequest,
  Position,
  PositionAvailableRestrictedColumns,
  PositionsSetValues,
} from '../model/positions.model'
import {
  AdvancedFilterContext,
  ExternalPositionExtToExtRequest,
  ExternalPositionRequest,
  PositionHistoryExtToExtRequest,
  PositionHistoryRequest,
  PositionsRequestContext,
  PositionsService,
} from './positions.service'

export const POSITIONS_URL = `helios-positions`
export const POSITIONS_HISTORY_URL = `${POSITIONS_URL}/history`
export const POSITIONS_HISTORY_EXT_TO_EXT_URL = `${POSITIONS_HISTORY_URL}/externalToExternal`
export const POSITIONS_COMPARISON_URL = `${POSITIONS_URL}/comparison`
export const POSITIONS_COMPARISON_EXT_TO_EXT_URL = `${POSITIONS_COMPARISON_URL}/externalToExternal`
export const EXTERNAL_POSITION_DETAIL_URL = `${POSITIONS_URL}/externalPositionDetail`
export const POSITIONS_SET_VALUES_URL = `${POSITIONS_URL}/setValues`
export const POSITIONS_AVAILABLE_ACTIONS = `${POSITIONS_URL}/availableActions`
export const POSITIONS_RESTRICTED_COLUMNS_URL = `${POSITIONS_URL}/positionRestrictedColumns`

export interface GetPositionsResponse {
  data: Position[]
}

@Injectable({
  providedIn: 'root',
})
export class HttpPositionsService extends PositionsService {
  constructor(private readonly http: HttpClient) {
    super()
  }

  getPositions(
    request: HeliosServerSideRequest<Position, PositionsRequestContext>,
  ): Observable<Position[]> {
    return this.http
      .post<GetPositionsResponse>(POSITIONS_URL, request)
      .pipe(map(response => response.data ?? []))
  }

  getHistoricalBalanceComparisons(
    request: PositionHistoryRequest,
  ): Observable<HistoricalBalanceComparison[]> {
    return this.http.post<HistoricalBalanceComparison[]>(
      POSITIONS_HISTORY_URL,
      request,
    )
  }

  getHistoricalBalanceComparisonsExtToExt(
    request: PositionHistoryExtToExtRequest,
  ): Observable<HistoricalBalanceComparisonExtToExt[]> {
    return this.http.post<HistoricalBalanceComparisonExtToExt[]>(
      POSITIONS_HISTORY_EXT_TO_EXT_URL,
      request,
    )
  }

  getExternalPositionDetail(
    positionId: number,
  ): Observable<ExternalPositionDetailResponse> {
    const options = {
      params: new HttpParams().append('positionId', positionId),
    }
    return this.http.get<ExternalPositionDetailResponse>(
      EXTERNAL_POSITION_DETAIL_URL,
      options,
    )
  }

  getPositionComparisons(
    request: ExternalPositionRequest,
  ): Observable<PositionComparisons> {
    return this.http.post<PositionComparisons>(
      POSITIONS_COMPARISON_URL,
      request,
    )
  }
  getPositionComparisonsExtToExt(
    request: ExternalPositionExtToExtRequest,
  ): Observable<PositionComparisonsExtToExt> {
    return this.http.post<PositionComparisonsExtToExt>(
      POSITIONS_COMPARISON_EXT_TO_EXT_URL,
      request,
    )
  }

  getPositionsByIds(
    positionIds: string[],
    advancedFilterContext: AdvancedFilterContext,
    fields?: string[],
  ): Observable<Position[]> {
    return this.getPositions(
      this.getHeliosRequestFromPositionIds(
        positionIds,
        advancedFilterContext,
        fields,
      ),
    ).pipe(take(1))
  }

  private getHeliosRequestFromPositionIds(
    positionIds: string[],
    advancedFilterContext: AdvancedFilterContext,
    fields?: string[],
  ) {
    //The backend handles the row range like this [startRow, endRow) ie include start row up until but not including endRow
    return {
      ...genericHeliosRequest,
      endRow: positionIds.length,
      fields,
      filter: {
        positionId: {
          filterType: 'set',
          values: positionIds,
        } as SetFilter,
      },
    } as HeliosServerSideRequest<Position, PositionsRequestContext>
  }

  getPositionsSetValues(): Observable<PositionsSetValues> {
    return this.http.get<PositionsSetValues>(POSITIONS_SET_VALUES_URL)
  }

  getPositionsExportExcel(request: ExportExcelRequest): Observable<Blob> {
    const options = {
      responseType: 'blob' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    const requestPayload = {
      ...genericHeliosRequest,
      headers: request.headers,
      fields: request.fields,
      context: request.context,
      filter: request.filters,
      userTimeZone: request.userTimeZone,
    }

    return this.http.post(`${POSITIONS_URL}/export`, requestPayload, options)
  }

  getPositionsAvailableActions(): Observable<string[]> {
    return this.http.get<string[]>(POSITIONS_AVAILABLE_ACTIONS)
  }

  getAvailableRestrictedColumns(): Observable<
    PositionAvailableRestrictedColumns[]
  > {
    return this.http.get<PositionAvailableRestrictedColumns[]>(
      POSITIONS_RESTRICTED_COLUMNS_URL,
    )
  }
}
